import React from "react";
import {
  Box,
  IconButton,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import Done from "@mui/icons-material/Done";
import Close from "@mui/icons-material/Close";

const toDateInputValue = (d) => {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(
    date.getMonth() + 1
  ).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
};

const fromDateInputValue = (s) => {
  if (!s) return null;
  const [yyyy, mm, dd] = s.split("-").map(Number);
  if (!yyyy || !mm || !dd) return null;
  return new Date(yyyy, mm - 1, dd);
};

export default function BulkColumnPopover({
  bulk,
  activeCol,
  inputRef,
  onKeyDown,
  onClose,
  onApply,
  onModeChange,
  onValueChange,
}) {
  const compactInputSx = {
    width: 138,
    borderRadius: 2,
    border: "1px solid rgba(0,0,0,0.14)",
    px: 1,
    py: 0.7,
    fontSize: 12,
    outline: "none",
    background: "#fff",
  };

  const renderInput = () => {
    if (!activeCol) return null;

    if (typeof activeCol.editor === "function") {
      return activeCol.editor({
        value: bulk.value,
        setValue: onValueChange,
        inputRef,
      });
    }

    if (activeCol.type === "date") {
      return (
        <Box
          component="input"
          ref={inputRef}
          type="date"
          value={toDateInputValue(bulk.value)}
          onChange={(e) =>
            onValueChange(fromDateInputValue(e.target.value))
          }
          sx={compactInputSx}
        />
      );
    }

    return (
      <Box
        component="input"
        ref={inputRef}
        type="text"
        value={bulk.value ?? ""}
        onChange={(e) => onValueChange(e.target.value)}
        sx={compactInputSx}
      />
    );
  };

  return (
    <Popover
      open={bulk.open}
      anchorReference="anchorPosition"
      anchorPosition={bulk.anchorPosition}
      onClose={onClose}
      transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      slotProps={{
        paper: {
          elevation: 2,
          sx: {
            p: 1,
            borderRadius: 2,
            background: "#fff",
            display: "flex",
            alignItems: "center",
            gap: 1,
          },
        },
      }}
    >
      <Box
        onKeyDown={onKeyDown}
        sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
      >
        {renderInput()}

        <ToggleButtonGroup
          exclusive
          size="small"
          value={bulk.mode}
          onChange={(e, v) => v && onModeChange(v)}
          sx={{
            height: 30,
            "& .MuiToggleButton-root": {
              fontSize: 11,
              px: 0.9,
              py: 0,
              textTransform: "none",
              borderRadius: 1.5,
              borderColor: "rgba(0,0,0,0.14)",
              "&.Mui-selected": {
                background: "linear-gradient(180deg,#e3f2fd,#bbdefb)",
                color: "#0d47a1",
                boxShadow: "inset 0 1px 3px rgba(0,0,0,0.25)",
              },
            },
          }}
        >
          <Tooltip
            title="Ersätt alla tomma celler"
            placement="top"
            arrow
          >
            <ToggleButton value="empty" selected={bulk.mode === "empty"}>
              Tomma
            </ToggleButton>
          </Tooltip>

          <Tooltip
            title="Ersätt alla celler"
            placement="top"
            arrow
          >
            <ToggleButton value="all" selected={bulk.mode === "all"}>
              Alla
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>

        <Tooltip title="Applicera (Enter)" arrow>
          <IconButton
            size="small"
            onClick={onApply}
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.5,
              background: "#1976d2",
              color: "#fff",
              "&:hover": { background: "#1565c0" },
            }}
          >
            <Done fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Stäng (Esc)" arrow>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ width: 28, height: 28 }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Popover>
  );
}